// Staff preview of the minted contract. It opens on Send, before the confirm,
// and shows a placeholder until the mint returns. With editable fields it is
// staff's form to fill: Confirm waits for its funder step.
import { DocusealForm } from "@docuseal/react";
import { Maximize2, Minimize2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ContractPreviewResponse } from "../types";

export type ContractPreviewModalProps = {
  open: boolean;
  preview: ContractPreviewResponse | null;
  confirming: boolean;
  /** Staff finished the funder step of an editable preview. */
  completed?: boolean;
  /** A preview action (reload, edit again) failed; the preview is unchanged. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  onCompleted?: () => void;
  onRefresh?: () => void;
  onEditAgain?: () => void;
  /** A document is emailed, not sent as a merchant flow: the copy says so. */
  isDocument?: boolean;
};

const COPY = {
  contract: {
    title: "Preview contract",
    noun: "contract",
    confirms: "sends the merchant flow",
  },
  document: {
    title: "Preview document",
    noun: "document",
    confirms: "emails the document to the merchant",
  },
} as const;

// The modal's copy for each state of the preview, in one place.
const EDIT_AGAIN_HINT = "Edit again changes the answers.";
const DESCRIPTION = {
  fill: (c: (typeof COPY)[keyof typeof COPY]) =>
    `Fill in the ${c.noun}'s open fields and finish the form. Confirm then ${c.confirms}; Cancel discards this preview.`,
  filled: (c: (typeof COPY)[keyof typeof COPY]) =>
    `Review the filled ${c.noun}. Confirm ${c.confirms}; ${EDIT_AGAIN_HINT} Cancel discards this preview.`,
  review: (c: (typeof COPY)[keyof typeof COPY]) =>
    `Review the filled ${c.noun}. Confirm ${c.confirms}; Cancel discards this preview.`,
  pdf: (c: (typeof COPY)[keyof typeof COPY]) =>
    `Review the ${c.noun} as it will be emailed. Confirm ${c.confirms}; Cancel discards this preview.`,
} as const;

/** Whether the embed credential's `expiresAt` has passed; flips when it does. */
function useExpired(expiresAt: string | undefined): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const remaining = expiresAt ? Date.parse(expiresAt) - Date.now() : Infinity;
    setExpired(remaining <= 0);
    if (remaining <= 0 || remaining === Infinity) return;
    const timer = window.setTimeout(() => setExpired(true), remaining);
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  return expired;
}

const ContractPreviewModal: React.FC<ContractPreviewModalProps> = ({
  open,
  preview,
  confirming,
  completed = false,
  error,
  onConfirm,
  onCancel,
  onCompleted,
  onRefresh,
  onEditAgain,
  isDocument = false,
}) => {
  const [fullscreen, setFullscreen] = useState(false);
  // The session clears in the render that closes the dialog; keep the last preview
  // through the exit animation so neither the form nor the button flashes.
  const [shown, setShown] = useState(preview);
  if (preview !== shown && (preview || open)) setShown(preview);
  // A PDF preview is always a document's (one with no fields: its PDF is what is emailed).
  const pdf = shown?.mode === "pdf";
  const copy = COPY[isDocument || pdf ? "document" : "contract"];
  const preparing = open && !shown;
  const fillable = Boolean(shown?.editable_fields?.length);
  // Edit again's previous answers, shown as editable defaults. DocuSeal reads
  // `default_value` though its typings omit it.
  const fields = useMemo(
    () =>
      Object.entries(shown?.field_defaults ?? {}).map(([name, default_value]) => ({
        name,
        default_value,
        readonly: false,
      })),
    [shown]
  );
  const expired = useExpired(shown?.expires_at);

  useEffect(() => {
    if (!open) setFullscreen(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !confirming) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={!confirming}
        className={cn(
          // A definite height gives the form's scroll container a bounded body.
          "flex flex-col gap-3",
          fullscreen
            ? "inset-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0 border-0 p-3 sm:rounded-none"
            : "h-[92vh] w-[min(72rem,calc(100vw-2rem))] max-w-none"
        )}
        onPointerDownOutside={(event) => {
          if (confirming) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          // Escape leaves fullscreen first; a second press closes the dialog.
          if (fullscreen) {
            event.preventDefault();
            setFullscreen(false);
          } else if (confirming) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="shrink-0 pr-8">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle>{copy.title}</DialogTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFullscreen((on) => !on)}
            >
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              {fullscreen ? "Exit fullscreen" : "Fullscreen"}
            </Button>
          </div>
          {/* For screen readers only: the header shows just the title and Fullscreen. */}
          <DialogDescription className="sr-only">
            {DESCRIPTION[pdf ? "pdf" : fillable ? (completed ? "filled" : "fill") : "review"](copy)}
          </DialogDescription>
        </DialogHeader>

        {expired && !completed && onRefresh ? (
          <div className="flex shrink-0 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
            <span>This form&apos;s session has expired. Reload it to keep going.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={confirming}
              onClick={onRefresh}
            >
              Reload form
            </Button>
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="shrink-0 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {/* The form lays every page out at full height and never scrolls itself. */}
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border bg-background">
          {shown?.mode === "pdf" ? (
            // The template's own PDF, fetched through the authenticated API: read-only.
            <iframe
              title="Document PDF"
              src={shown.object_url}
              className="h-full min-h-[70vh] w-full"
            />
          ) : shown ? (
            // `src` names only the DocuSeal host; the form reads the slug from the token.
            <DocusealForm
              // A new credential or submission remounts the form.
              key={shown.embed_token}
              src={shown.src}
              token={shown.embed_token}
              preview={!fillable}
              fields={fillable ? fields : undefined}
              // Reopened mid-way, start at the first open field, not the first unanswered.
              goToLast={fillable ? false : undefined}
              withDownloadButton={false}
              withSendCopyButton={false}
              withDecline={false}
              allowToResubmit={false}
              completedMessage={{
                title: "Fields saved",
                body: `Confirm ${copy.confirms}. ${EDIT_AGAIN_HINT}`,
              }}
              onComplete={() => onCompleted?.()}
              onLoad={(data) => {
                // A reload after staff finished (maybe in another tab) opens on "submitted".
                if (fillable && data.completed_submitter && !data.submitter) onCompleted?.();
              }}
            />
          ) : (
            preparing && (
              <p className="p-4 text-sm text-muted-foreground">Preparing the contract…</p>
            )
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" disabled={confirming} onClick={onCancel}>
            Cancel
          </Button>
          {fillable && completed && onEditAgain ? (
            <Button type="button" variant="outline" disabled={confirming} onClick={onEditAgain}>
              Edit again
            </Button>
          ) : null}
          {/* No preview yet: `confirming` is the mint, not the send. */}
          <Button
            type="button"
            disabled={confirming || !preview || (fillable && !completed)}
            onClick={() => onConfirm()}
          >
            {preparing ? "Preparing…" : confirming ? "Sending…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractPreviewModal;
